import { AssessmentSubtype, AssessmentType, QuestionSelectDto, QuestionType } from '@@types/api.types';
import { SurveyLayout } from '@components/layouts/survey.layout';
import { AssessmentQuestionShell } from '@components/pages/assessment-shell/components/assessment-question-shell.component';
import { HelmetWrapper } from '@components/shared/helmet.component.tsx';
import { PageviewTracker } from '@components/shared/pageview-tracker.component.tsx';
import { setReturnToSearchParam, useSafeReturnTo } from '@hooks/return-to.hook';
import { errorToast } from '@hooks/toast.hook';
import { useGetSurvey } from '@queries/survey.query';
import { useSurveyAttachmentsStore } from '@stores/survey-attachments.store';
import { getQuestionIndexInSurvey, isQuestionAnswered } from '@utils/survey.util';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnswerResponseProgress } from '../../../types/answer.types';
import { QuestionTrlCharacteristicsComponent } from '../survey/components/question-trl-characteristics.component';
import { QuestionComponent } from '../survey/components/question.component';

export const QuestionPage: React.FC = () => {
  const { surveyId, technologyId, sectionId } = useParams();
  const { returnTo, returnToSearch } = useSafeReturnTo();

  const { data: survey } = useGetSurvey(surveyId);
  const section = survey?.snapshot.chapters.flatMap((chapter) => chapter.sections).find((sec) => sec.id === sectionId);

  const navigate = useNavigate();

  const resetCurrentQuestionIndex = () => {
    if (section && survey?.answers && survey?.questions) {
      const firstUnansweredIndex = section.questions.findIndex((questionId) => {
        if (!sectionId) return false;
        const question = survey.questions.find((q) => q.id === questionId);
        return !isQuestionAnswered({
          answers: survey.answers,
          questionId,
          sectionId,
          questionType: question?.type,
        });
      });
      return firstUnansweredIndex !== -1 ? firstUnansweredIndex : 0;
    }

    return 0;
  };

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(resetCurrentQuestionIndex());

  // biome-ignore lint/correctness/useExhaustiveDependencies: When a new section is loaded, we need to reset the current question index
  useEffect(() => {
    setCurrentQuestionIndex(resetCurrentQuestionIndex());
  }, [section]);

  // Get the questionId for the current position in the current section
  const currentQuestionId = section?.questions[currentQuestionIndex];

  // Find the question object using both questionId AND sectionId context
  const currentQuestion = survey?.questions.find((question) => question.id === currentQuestionId);
  const currentAnswer = survey?.answers.find((answer) => {
    if (!sectionId || !currentQuestionId) {
      return false;
    }

    return answer.questionId === currentQuestionId && answer.sectionId === sectionId;
  });
  const questionAttachmentKey =
    currentQuestion?.id && sectionId && surveyId ? `${surveyId}:${sectionId}:${currentQuestion.id}` : undefined;
  const bufferedAttachmentCount = useSurveyAttachmentsStore((state) => {
    if (!questionAttachmentKey) {
      return 0;
    }

    return state.bufferedByQuestion[questionAttachmentKey]?.length ?? 0;
  });
  const hasPendingBufferWithoutAnswer = Boolean(!currentAnswer?.id && bufferedAttachmentCount > 0);

  // Get sections flattened across all chapters
  const sectionsFlat = survey?.snapshot.chapters.flatMap((chapter) => chapter.sections) ?? [];
  const currentSectionIndex = sectionsFlat.findIndex((sec) => sec.id === sectionId);

  // Previous question logic - first check within the same section
  const prevQuestionId = section?.questions[currentQuestionIndex - 1];
  let previousQuestion: QuestionSelectDto | undefined;

  if (prevQuestionId) {
    previousQuestion = survey?.questions.find((question) => question.id === prevQuestionId);
  } else if (currentSectionIndex > 0) {
    // If at the beginning of the section, get the last question of the previous section
    const prevSection = sectionsFlat[currentSectionIndex - 1];
    const prevSectionLastQuestionId = prevSection?.questions[prevSection.questions.length - 1];
    previousQuestion = survey?.questions.find((question) => question.id === prevSectionLastQuestionId);
  }

  const options =
    currentQuestion?.type === QuestionType.PROGRESS
      ? [AnswerResponseProgress.YES, AnswerResponseProgress.NO, AnswerResponseProgress.IN_PROGRESS]
      : currentQuestion?.type === QuestionType.PROGRESS_OPTIONAL
        ? [
            AnswerResponseProgress.YES,
            AnswerResponseProgress.NO,
            AnswerResponseProgress.IN_PROGRESS,
            AnswerResponseProgress.NOT_APPLICABLE,
          ]
        : [];

  const questionLabels = section?.questions.map((questionId, index) => {
    const indexInSurvey = getQuestionIndexInSurvey({
      sectionId,
      snapshot: survey?.snapshot,
      questionId,
    });

    return {
      isComplete: Boolean(
        isQuestionAnswered({
          answers: survey?.answers,
          questionId,
          sectionId,
          questionType: currentQuestion?.type,
        }),
      ),
      label: ((indexInSurvey ?? index) + 1).toString(),
    };
  });

  const progress = questionLabels
    ? questionLabels.filter((label) => label.isComplete).length / Math.max(questionLabels.length, 1)
    : 1;

  const currentChapter = survey?.snapshot.chapters.find((chapter) =>
    chapter.sections.some((sec) => sec.id === sectionId),
  );

  const currentSectionIndexInChapter = currentChapter?.sections.findIndex((sec) => sec.id === sectionId) || 0;
  const chapterNumber =
    currentChapter?.id && survey
      ? survey.snapshot.chapters.findIndex((chapter) => chapter.id === currentChapter.id) + 1
      : 1;

  const handlePreviousQuestion = () => {
    const current = currentQuestionIndex;
    const prev = current - 1;

    if (prev < 0) {
      // We're at the first question of this section
      if (currentSectionIndex > 0) {
        // Navigate to the last question of the previous section
        const prevSection = sectionsFlat[currentSectionIndex - 1];
        navigate(`/technology/${technologyId}/survey/${surveyId}/section/${prevSection.id}${returnToSearch}`);
      } else {
        // If we're already at the first section, just stay at the first question
        setCurrentQuestionIndex(0);
      }
    } else {
      // Move to the previous question in the current section
      setCurrentQuestionIndex(prev);
    }
  };

  const handleNextQuestion = (skipToQuestionId?: string) => {
    if (skipToQuestionId) {
      // Find the index of the question to skip to within the current section
      const skipToIndex = section?.questions.findIndex((qId) => qId === skipToQuestionId);

      if (skipToIndex !== undefined && skipToIndex !== -1) {
        // If the question is in the current section, jump to it
        setCurrentQuestionIndex(skipToIndex);
        return;
      }

      // If the question is not in the current section, find which section contains it
      const targetSection = sectionsFlat.find((sec) => sec.questions.some((qId) => qId === skipToQuestionId));

      if (targetSection) {
        // Navigate to the section containing the target question
        navigate(`/technology/${technologyId}/survey/${surveyId}/section/${targetSection.id}${returnToSearch}`);
        return;
      }
    }

    const current = currentQuestionIndex;
    const next = current + 1;

    if (next >= (section?.questions?.length || 0)) {
      // We're at the last question of this section
      if (currentSectionIndex < sectionsFlat.length - 1) {
        // Navigate to the first question of the next section
        navigate(
          `/technology/${technologyId}/survey/${surveyId}/section/${sectionsFlat[currentSectionIndex + 1].id}${returnToSearch}`,
        );
      } else {
        // If we're at the last section, navigate back to survey overview
        navigate(`/technology/${technologyId}/survey/${surveyId}${returnToSearch}`);
      }
    } else {
      // Move to the next question in the current section
      setCurrentQuestionIndex(next);
    }
  };

  const handleSlotClick = (index: number) => {
    if (hasPendingBufferWithoutAnswer) {
      errorToast({ description: 'Answer should be submitted in order to save attachment.' });
      return;
    }

    if (index >= 0 && index < (section?.questions.length || 0)) {
      setCurrentQuestionIndex(index);
    }
  };

  const isMilitaryTrl =
    survey?.assessmentType === AssessmentType.TRL && survey?.assessmentSubtype === AssessmentSubtype.MILITARY;
  const title = `${isMilitaryTrl ? 'Military TRL' : (survey?.assessmentType ?? '')} Questionnaire`.trim();
  const breadcrumbSearchParams = new URLSearchParams();
  if (currentChapter?.id) {
    breadcrumbSearchParams.set('chapterId', currentChapter.id);
  }
  setReturnToSearchParam({ returnTo, searchParams: breadcrumbSearchParams });
  const breadcrumbSearch = breadcrumbSearchParams.toString();
  const breadcrumbHref = `/technology/${technologyId}/survey/${surveyId}${breadcrumbSearch ? `?${breadcrumbSearch}` : ''}`;

  return (
    <PageviewTracker>
      <HelmetWrapper title={title} />
      <SurveyLayout>
        <AssessmentQuestionShell
          currentQuestionIndex={currentQuestionIndex}
          headerItems={[
            {
              href: breadcrumbHref,
              label: `Part ${chapterNumber}`,
            },
            { label: `Section ${currentSectionIndexInChapter + 1}` },
          ]}
          onSlotClick={handleSlotClick}
          progress={progress}
          questionLabels={questionLabels}
          sectionIndex={currentSectionIndexInChapter}
          sectionTitle={section?.name || ''}
        >
          {currentQuestion &&
            section &&
            (currentQuestion.type === QuestionType.TRL_CHARACTERISTICS ? (
              <QuestionTrlCharacteristicsComponent
                answers={survey?.answers}
                key={currentQuestion.id + '-' + sectionId}
                handleNextQuestion={handleNextQuestion}
                handlePreviousQuestion={handlePreviousQuestion}
                previousQuestion={previousQuestion}
                question={currentQuestion}
                section={section}
                sectionId={sectionId}
                surveyId={surveyId}
              />
            ) : (
              <QuestionComponent
                answers={survey?.answers}
                key={currentQuestion.id + '-' + sectionId}
                handleNextQuestion={handleNextQuestion}
                handlePreviousQuestion={handlePreviousQuestion}
                previousQuestion={previousQuestion}
                question={currentQuestion}
                questionOptionsValues={options}
                sectionId={sectionId}
                surveyId={surveyId}
              />
            ))}
        </AssessmentQuestionShell>
      </SurveyLayout>
    </PageviewTracker>
  );
};
